"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { WorkshopTag } from "@/components/Craft";
import Wand from "@/components/Wand";
import type { Project } from "@/lib/projects";
import { d } from "@/lib/site";

const fig = "relative m-0 aspect-[4/3] overflow-hidden rounded-[6px] bg-muted";
const pic = "block h-full w-full object-contain";

// Project popup over the gallery: photos, workshop tag, what we did, prev/next (also arrow keys), Esc closes.
// A native modal <dialog>: the page behind is inert, so focus stays inside.
export default function GalleryModal({
  project: p,
  onClose,
  onPrev,
  onNext,
}: {
  project: Project;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  // Opening animates by CSS (.gdlg[open]); closing plays the reverse first (.gdlg-out), then unmounts.
  const [closing, setClosing] = useState(false);
  // which way the last switch went, for the slide-in direction
  const [dir, setDir] = useState<"prev" | "next">();
  const prev = () => {
    setDir("prev");
    onPrev();
  };
  const next = () => {
    setDir("next");
    onNext();
  };
  function close() {
    if (closing) return;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return onClose();
    setClosing(true);
    setTimeout(onClose, 220);
  }

  useEffect(() => {
    const dlg = ref.current!;
    if (!dlg.open) dlg.showModal();
    const root = document.documentElement;
    const overflow = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      root.style.overflow = overflow;
      if (dlg.open) dlg.close();
    };
  }, []);

  // a new project starts at the top of the panel
  useEffect(() => {
    panel.current?.scrollTo({ top: 0 });
  }, [p.slug]);

  // workshop tag of a work without before photo (with one, the Zauberstab brings its own); no "Möbel" row, the
  // title stands right above it
  const rows = [
    { label: "Alter", value: p.alter },
    { label: "Zustand", value: p.zustand },
    { label: "Arbeitszeit", value: p.hours },
    { label: "Material", value: p.materials },
    { label: "Bezug", value: p.cover },
  ].filter((r): r is { label: string; value: string } => !!r.value);

  // A bracket in the title ("Rokoko-Sofa (Restaurierungsarbeit für das Schloss …)") becomes a small second line
  const cut = p.title.indexOf(" (");
  const main = cut > 0 ? p.title.slice(0, cut) : p.title;
  const note = cut > 0 ? p.title.slice(cut + 1) : "";
  const title = (
    <h2 id="gdlg-title" className={`m-0 leading-[1.15] [text-wrap:wrap] ${main.length > 30 ? "text-[clamp(22px,2.2vw,28px)]" : "text-[clamp(24px,3vw,34px)]"}`}>
      {main}
      {note && <span className="mt-1.5 block text-[clamp(16px,1.5vw,19px)] leading-[1.3] font-normal text-ink-2">{note}</span>}
    </h2>
  );
  const closeButton = (
    <button
      type="button"
      onClick={close}
      aria-label="Schließen"
      autoFocus
      className="flex h-11 w-11 flex-none cursor-pointer items-center justify-center rounded-full border-[1.5px] border-line-strong bg-surface text-ink"
    >
      <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <path d="M6 6l12 12M18 6 6 18" />
      </svg>
    </button>
  );
  // the job, and under it the call to action as a plain link
  const done = (
    <div className="flex flex-col gap-3">
      {p.aufwand && (
        <div className="flex flex-col gap-1">
          <h3 className="m-0 text-xl">Der Auftrag</h3>
          <p className="m-0 max-w-[90ch] text-ink-2">{p.aufwand}</p>
        </div>
      )}
      <Link href="/kontakt/" className="self-start py-1 font-semibold text-accent no-underline hover:underline">
        Inspiriert? Schicken Sie uns ein Foto Ihres Möbels →
      </Link>
    </div>
  );
  // previous / next: round arrow buttons in the dark margins left and right of the panel (phones: over its edges)
  const arrow = (d: "prev" | "next") => (
    <button
      type="button"
      onClick={d === "prev" ? prev : next}
      aria-label={d === "prev" ? "Vorheriges Möbel" : "Nächstes Möbel"}
      className={`gdlg-arrow absolute top-1/2 z-40 flex size-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-[1.5px] border-white/30 bg-black/45 text-white backdrop-blur-sm hover:bg-black/70 ${
        d === "prev" ? "left-2 sm:left-5" : "right-2 sm:right-5"
      }`}
    >
      <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d={d === "prev" ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} />
      </svg>
    </button>
  );

  return (
    <dialog
      ref={ref}
      className={`gdlg pol ${closing ? "gdlg-out" : ""}`}
      aria-labelledby="gdlg-title"
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
      onClick={(e) => e.target === e.currentTarget && close()}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") prev();
        else if (e.key === "ArrowRight") next();
      }}
    >
      <div
        ref={panel}
        // fixed height (the whole window), so it doesn't change size from one work to the next
        className="gdlg-panel relative box-border flex h-full w-full max-w-[1400px] flex-col gap-5 overflow-x-hidden overflow-y-auto rounded-xl bg-bg p-5 text-ink sm:p-7"
      >
        <div className="absolute top-4 right-4 z-30 sm:top-5 sm:right-5">{closeButton}</div>
        {/* the work's content fades and slides in from the side it came from (key: a new work starts fresh) */}
        <div key={p.slug} className="gdlg-swap my-auto flex flex-col gap-5" data-dir={dir}>
          {p.before ? (
            // photo large on the left, title, hint and workshop tag on the right, the job under the photo
            <Wand items={[p]} startBefore showTag side={{ top: <div className="pr-14">{title}</div>, bottom: done }} />
          ) : (
            // no before photo, so no Zauberstab: the mirror of that layout, title and workshop tag on the left,
            // photo(s) and the job on the right (phones: title, photos, tag, job)
            <div className="flex flex-col gap-5 min-[900px]:grid min-[900px]:grid-cols-[400px_minmax(0,1fr)] min-[900px]:grid-rows-[auto_1fr] min-[900px]:items-start min-[900px]:gap-x-10 min-[1280px]:grid-cols-2">
              <div className="order-2 grid gap-3 min-[900px]:col-start-2 min-[900px]:row-span-2 min-[900px]:row-start-1 min-[900px]:pr-14 min-[1280px]:w-full min-[1280px]:justify-self-start">
                <div className={`grid gap-3 ${p.extra ? "sm:grid-cols-2" : ""}`}>
                  {/* no taller than the window leaves room for */}
                  <figure className={`${fig} max-h-[calc(100dvh-340px)]`} style={p.extra ? undefined : { aspectRatio: p.ratio }}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={d(p.after)} alt={`${p.title}, fertig gepolstert`} className={pic} />
                  </figure>
                  {p.extra && (
                    <figure className={fig}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={d(p.extra)} alt={`${p.title}, Detail`} className={pic} />
                    </figure>
                  )}
                </div>
                {/* phones: the job comes after the workshop tag (see below) */}
                <div className="hidden min-[900px]:block">{done}</div>
              </div>
              {/* from 1280 px title + tag are one block, centred vertically beside the (taller) photo column; below that
                  the wrapper vanishes (display: contents) and they sit in their own grid cells */}
              <div className="contents min-[1280px]:col-start-1 min-[1280px]:row-span-2 min-[1280px]:row-start-1 min-[1280px]:flex min-[1280px]:w-[400px] min-[1280px]:flex-col min-[1280px]:gap-5 min-[1280px]:self-center min-[1280px]:justify-self-end">
                <div className="order-1 pr-14 min-[900px]:col-start-1 min-[900px]:row-start-1 min-[900px]:pr-0 min-[1280px]:w-max min-[1280px]:min-w-full min-[1280px]:max-w-[calc(100%_+_180px)] min-[1280px]:self-end min-[1280px]:pr-5 min-[1280px]:text-right">{title}</div>
                {/* where the Zauberstab hint would be */}
                <p
                  className="order-3 m-0 min-[900px]:hidden min-[1280px]:block text-[24px] leading-[1.1] font-semibold text-ink-3 min-[1280px]:order-2 min-[1280px]:w-[380px] min-[1280px]:max-w-full min-[1280px]:text-center"
                  style={{ fontFamily: "var(--font-caveat), cursive" }}
                >
                  (kein Vorher-Foto vorhanden)
                </p>
                {rows.length > 0 && (
                  <div className="wz-tag-side order-4 min-[1280px]:order-3 min-[900px]:col-start-1 min-[900px]:row-start-2 min-[1280px]:w-full">
                    <WorkshopTag rows={rows} className="wand-tag" />
                  </div>
                )}
              </div>
              <div className="order-5 min-[900px]:hidden">{done}</div>
            </div>
          )}
        </div>
      </div>
      {arrow("prev")}
      {arrow("next")}
    </dialog>
  );
}
